/**
 * secciones.js — El `id` de la <section> de cada bloque: la SSOT de lo que emiten los renders.
 *
 * Módulo PURO (sin DOM ni `window`, sin imports): lo ejercitan en node los arneses de tests/
 * (_pie_enlaces.mjs, _secciones.mjs); por eso va en ES2019 sin `?.` ni `??`: el node de los tests
 * es el 12.
 *
 * Cada render pinta su sección con un id FIJO de dos formas, según el tipo:
 *   · `bloque-<tipo>`           → hero, content, features, gallery, maps, pricing, banner…
 *   · `bloque-<tipo>-<índice>`  → form, reservas, menu, services, process, bento…
 * El ÍNDICE es la posición del bloque en `page.blocks`, con la cabecera en el 0: el mismo número
 * que `render.js` y `compileSiteHtml` le pasan al render. Por eso esos ids CAMBIAN al insertar,
 * borrar o mover un bloque por encima, y un ancla guardada con el número muere sola: el pie los
 * vuelve a resolver en cada sincronización (utils/anclas.js), no los guarda como verdad.
 * Cabecera, pie y barra de conversión no llevan id. Dos bloques del mismo tipo con id fijo repiten
 * id, y el navegador va al primero.
 *
 * Los renders NO leen de aquí (cada uno escribe su literal): lo que ata las dos cosas es
 * `tests/test_anclas_pie.py`, que pinta los 34 renders del registro y compara el id que sale con
 * `idDeSeccion`. Un bloque nuevo tiene que entrar en una de las tres listas o ese test falla.
 */

/** Tipos cuyo render pinta `bloque-<tipo>`. */
export const TIPOS_ID_FIJO = [
    'hero', 'content', 'features', 'stats', 'faq', 'pricing', 'testimonials', 'timeline', 'team',
    'logos', 'catalogo', 'maps', 'video', 'gallery', 'banner', 'before-after', 'countdown',
];

/** Tipos cuyo render pinta `bloque-<tipo>-<índice>`. */
export const TIPOS_ID_CON_INDICE = [
    'form', 'reservas', 'menu', 'services', 'process', 'bento', 'trust', 'journey', 'comparison',
    'imagen', 'carrusel', 'reserva-live', 'entradas', 'reviews', 'ai-custom',
    'ofertas', 'novedades', 'top', 'habitual',
];

/** Tipos que no pintan ninguna <section> con id. */
export const TIPOS_SIN_ID = ['header', 'footer', 'cta-bar'];

/**
 * Anclas que siempre llevan a algún sitio aunque ningún bloque se llame así: el <main
 * id="contenido"> que `compileSiteHtml` hornea (el destino del «saltar al contenido») y `#top`,
 * que el navegador resuelve siempre al principio del documento.
 */
export const ANCLAS_DE_PAGINA = ['contenido', 'top'];

/** El id de la sección de un bloque de `tipo` en la posición `indice` de su página, o null. */
export function idDeSeccion(tipo, indice) {
    if (TIPOS_ID_FIJO.indexOf(tipo) !== -1) return 'bloque-' + tipo;
    if (TIPOS_ID_CON_INDICE.indexOf(tipo) !== -1) return 'bloque-' + tipo + '-' + indice;
    return null;
}

/*
 * ANCLA PROPIA de una sección (`props.ancla`, 19-10-2026, decisión del dueño): el nombre fijo
 * (`carta`, `reservas`) que recibe una sección cuando se la enlaza desde la cabecera o desde un
 * botón (utils/ancla-seccion.js). NO sustituye al id de la <section>: site.css tiene ~117 reglas
 * atadas a `#bloque-<tipo>` y el runtime publicado saca el tipo del bloque de ese id. Va como un
 * <span id="carta" class="n3i-ancla"> vacío dentro de la sección (render.js y compileSiteHtml,
 * `inyectarAncla`), fuera de flujo: el navegador salta a él sin JS, también desde otra página
 * (`/#carta`). Sigue a la sección aunque se mueva; los ids con número no.
 *
 * Forma: minúsculas, cifras y guiones, empieza por letra, 1-40 caracteres, y nada que pueda chocar
 * con un id que ya pinta la página (los de las secciones, los de los formularios y las FAQ, el
 * <main id="contenido">, el lienzo del editor).
 */
const ANCLA_RE = /^[a-z](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const ANCLAS_RESERVADAS = ['contenido', 'top', 'resultado', 'main', 'header', 'footer'];
const PREFIJOS_RESERVADOS = ['bloque-', 'form-', 'faq-', 'n3i-'];

/** ¿Vale `v` como ancla propia de una sección? */
export function anclaValida(v) {
    if (typeof v !== 'string' || !ANCLA_RE.test(v) || ANCLAS_RESERVADAS.indexOf(v) !== -1) return false;
    for (let i = 0; i < PREFIJOS_RESERVADOS.length; i++) {
        if (v.indexOf(PREFIJOS_RESERVADOS[i]) === 0) return false;
    }
    return true;
}

/**
 * El ancla propia del bloque `indice` de `blocks`, o null: la de su `props.ancla` si es válida, si su
 * tipo pinta una sección con id y si ningún bloque ANTERIOR de la página la lleva ya (una sección
 * copiada a mano repetiría el id; el navegador iría a la primera, así que es la primera la que se
 * queda con él).
 */
export function anclaPropia(blocks, indice) {
    const lista = Array.isArray(blocks) ? blocks : [];
    const b = lista[indice];
    const a = b && b.props ? b.props.ancla : null;
    if (!anclaValida(a) || !idDeSeccion(b.type, indice)) return null;
    for (let j = 0; j < indice; j++) {
        const o = lista[j];
        if (o && o.props && o.props.ancla === a && idDeSeccion(o.type, j)) return null;
    }
    return a;
}

/** Todas las anclas que existen en una página: los ids de sus secciones, sus anclas propias y
 *  `ANCLAS_DE_PAGINA`. */
export function anclasDePagina(page) {
    const ids = new Set(ANCLAS_DE_PAGINA);
    const blocks = page && Array.isArray(page.blocks) ? page.blocks : [];
    blocks.forEach((b, i) => {
        const id = b ? idDeSeccion(b.type, i) : null;
        if (id) ids.add(id);
        const propia = anclaPropia(blocks, i);
        if (propia) ids.add(propia);
    });
    return ids;
}

/**
 * El id de la PRIMERA sección de la página cuyo tipo esté en `tipos`, probando los tipos en ese
 * orden (el primero de la lista que tenga bloque gana). null si no hay ninguna.
 */
export function primeraSeccion(page, tipos) {
    const blocks = page && Array.isArray(page.blocks) ? page.blocks : [];
    for (let t = 0; t < tipos.length; t++) {
        for (let i = 0; i < blocks.length; i++) {
            if (blocks[i] && blocks[i].type === tipos[t]) {
                const id = idDeSeccion(tipos[t], i);
                if (id) return id;
            }
        }
    }
    return null;
}
